import { useState } from "react";
import { Download, Trash2, CheckCircle2, Undo2, ChevronDown, ChevronUp, FileText } from "lucide-react";
import { fileUrl, statementPdfUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { money, PayBadge } from "./common";
import { t, fmtDate, getLang } from "@/i18n";

const Lines = ({ s }) => (
  <div className="space-y-1 text-xs" data-testid={`statement-lines-${s.id}`}>
    <p className="text-muted-foreground uppercase tracking-wider text-[10px]">{t("Detalle de lo asignado")}</p>
    <div className="max-h-56 overflow-auto rounded-md border border-border">
      <table className="w-full">
        <tbody>
          {s.lines.map((l, i) => (
            <tr key={i} className="border-b border-border/60 last:border-0">
              <td className="px-2 py-1"><span className="font-medium">{l.release_title}</span>{l.track_title ? <span className="text-muted-foreground"> · {l.track_title}</span> : null}</td>
              <td className="px-2 py-1 font-mono text-muted-foreground">{l.isrc || l.upc}</td>
              <td className="px-2 py-1 text-right tabular-nums">{money(l.amount, s.currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {s.notes && <p className="text-muted-foreground">{s.notes}</p>}
  </div>
);

export const StatementsTable = ({ items, admin, onMarkPaid, onUnpay, onDelete }) => {
  const [open, setOpen] = useState(null);
  const cols = (admin ? 9 : 8);
  return (
    <div className="panel overflow-hidden">
      <Table data-testid="statements-table">
        <TableHeader>
          <TableRow>
            {admin && <TableHead>{t("Artista")}</TableHead>}
            <TableHead>{t("Periodo")}</TableHead>
            <TableHead className="text-right">{t("Bruto")}</TableHead>
            <TableHead className="text-right">{t("Retención")}</TableHead>
            <TableHead className="text-right">{t("Neto a pagar")}</TableHead>
            <TableHead>{t("Estado")}</TableHead>
            <TableHead>{t("Pago")}</TableHead>
            <TableHead className="text-right">{t("Comprobante")}</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 && <TableRow><TableCell colSpan={cols} className="text-center text-sm text-muted-foreground py-8" data-testid="statements-empty">{t("Todavía no hay statements")}</TableCell></TableRow>}
          {items.map((s) => {
            const isOpen = open === s.id;
            const hasLines = s.lines?.length > 0;
            return [
              <TableRow key={s.id} data-testid={`statement-row-${s.id}`}>
                {admin && <TableCell className="font-medium">{s.user_name}</TableCell>}
                <TableCell className="font-medium">{s.period}</TableCell>
                <TableCell className="text-right tabular-nums">{money(s.gross, s.currency)}</TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">{money(s.withholding, s.currency)} <span className="text-[10px]">({s.withholding_pct}%)</span></TableCell>
                <TableCell className="text-right tabular-nums font-semibold" data-testid={`statement-net-${s.id}`}>{money(s.net, s.currency)}</TableCell>
                <TableCell><PayBadge status={s.status} testId={`statement-status-${s.id}`} /></TableCell>
                <TableCell className="text-xs text-muted-foreground">{s.status === "paid" ? <>{fmtDate(s.paid_at)}{s.reference ? <> · {s.reference}</> : null}</> : "—"}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <a href={statementPdfUrl(s.id, getLang())} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`statement-pdf-${s.id}`}><FileText className="h-3 w-3" /> PDF</a>
                  {s.file_id ? <a href={fileUrl(s.file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline ml-3" data-testid={`statement-file-${s.id}`}><Download className="h-3 w-3" /> {t("Reporte")}</a> : null}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {hasLines && <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => setOpen(isOpen ? null : s.id)} data-testid={`statement-toggle-${s.id}`}>{isOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />} {t("Detalle")} ({s.lines.length})</Button>}
                  {admin && (s.status === "pending" ? <Button size="sm" variant="outline" className="gap-1 h-7 ml-1" onClick={() => onMarkPaid(s)} data-testid={`statement-pay-${s.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> {t("Marcar pagado")}</Button>
                    : <Button size="sm" variant="ghost" className="gap-1 h-7 ml-1" onClick={() => onUnpay(s)} data-testid={`statement-unpay-${s.id}`}><Undo2 className="h-3.5 w-3.5" /> {t("Deshacer")}</Button>)}
                  {admin && <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => onDelete(s)} data-testid={`statement-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                </TableCell>
              </TableRow>,
              isOpen && hasLines && <TableRow key={`${s.id}-l`} className="bg-muted/20 hover:bg-muted/20"><TableCell colSpan={cols} className="p-4"><Lines s={s} /></TableCell></TableRow>,
            ];
          })}
        </TableBody>
      </Table>
    </div>
  );
};
